import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, Users, Lock, FileText, Target, Eye, 
  CheckCircle, ArrowRight, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <Helmet>
        <title>About ActionFraudOne | Fraud Investigation & Scam Protection</title>
        <meta name="description" content="ActionFraudOne helps individuals and businesses identify scams, investigate fraud, and protect communities from online crime." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <Shield className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-medium">About ActionFraudOne</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Fraud Investigation & Scam Protection
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed">
            ActionFraudOne was created to help individuals and businesses identify scams, 
            investigate fraud activity, and protect communities from online crime.
          </p>
        </div>

        {/* Mission */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-gradient-to-r from-gold/10 to-transparent border-gold/30">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-3">Our Mission</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We believe everyone deserves protection from fraud. Our mission is to provide accessible, 
                    professional fraud investigation services that help victims understand what happened, 
                    document evidence, and take informed action. We aim to make professional scam analysis 
                    available to individuals who might otherwise have no recourse.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: 'Professionalism',
                description: 'Every case receives thorough, professional analysis from experienced fraud investigators.'
              },
              {
                icon: Lock,
                title: 'Confidentiality',
                description: 'All client information is handled with strict confidentiality and data protection.'
              },
              {
                icon: Eye,
                title: 'Transparency',
                description: 'Clear communication about our services, pricing, and what clients can realistically expect.'
              }
            ].map((value, index) => (
              <Card key={index} className="bg-[#111] border-gold/10">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Our Team</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vanessa */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 bg-gold/10 rounded-full flex items-center justify-center mb-6 border-2 border-gold/30">
                    <User className="w-16 h-16 text-gold/50" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">Vanessa</h3>
                  <p className="text-gold font-medium mb-4">Fraud Investigation Specialist</p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Responsible for analysing scam cases and compiling comprehensive investigation reports. 
                    Vanessa brings expertise in fraud pattern recognition and evidence documentation 
                    to ensure clients receive thorough, actionable reports.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Moeez */}
            <Card className="bg-[#111] border-gold/20">
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 bg-gold/10 rounded-full flex items-center justify-center mb-6 border-2 border-gold/30">
                    <User className="w-16 h-16 text-gold/50" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">Moeez</h3>
                  <p className="text-gold font-medium mb-4">Digital Fraud Analyst</p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Responsible for analysing scam websites, communications, and online fraud patterns. 
                    Moeez specialises in digital footprint investigation and identifying connections 
                    between fraudulent entities.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What We Do */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What We Do</h2>
          <div className="space-y-4">
            {[
              {
                title: 'Scam Check Service',
                description: 'Quick verification of suspicious messages, emails, websites, and investment offers. Professional risk assessment within 24-48 hours.',
              },
              {
                title: 'Fraud Investigation Dossier',
                description: 'Comprehensive investigation reports documenting scam activity, digital footprints, and evidence suitable for legal or police use.',
              },
              {
                title: 'Scam Awareness Education',
                description: 'Free educational resources helping the public understand common scam tactics and protect themselves from fraud.',
              }
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-5 bg-white/5 rounded-lg border border-gold/10">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto text-center">
          <Card className="bg-gradient-to-r from-gold/10 to-transparent border-gold/30">
            <CardContent className="p-8">
              <Shield className="w-12 h-12 text-gold mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Need Help With a Potential Scam?</h2>
              <p className="text-gray-400 mb-6">
                Whether you've received a suspicious message or lost money to fraud, 
                we're here to help you understand what happened and what you can do next.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/scam-check">
                  <Button className="bg-gold hover:bg-gold/90 text-black font-bold">
                    Check a Suspicious Message <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-gold/50 text-gold hover:bg-gold/10">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;
